import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  LanguageString,
} from '../../../../common/components/dashboards/types';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import { TimePeriodSegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-segmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  TimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { StoryboardDateUtils } from '../../utils';

const OPERATIONAL_JOB_GRADE_FILTER = `EMPLOYEE.JOB_GRADE_LEVEL_1 = 'OPERATIONAL'`;
const NOT_OPERATIONAL_JOB_GRADE_FILTER = `EMPLOYEE.JOB_GRADE_LEVEL_1 != 'OPERATIONAL'`;
const NON_NULL_FUNCTION_FILTER = `EMPLOYEE.FUNCTION_LEVEL_1 IS NOT NULL`;

const YOC_AGENCY_STAFF_MP_FILTER = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_YOC' AND 
(EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'AS/RS' 
OR EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'MPS-YH') AND ${NON_NULL_FUNCTION_FILTER}`;
const JPHIL_CONTRACT_STAFF_FILTER = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JPHIL' 
AND EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'Contract Employee' 
AND EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_2 = 'JPHIL' AND ${NON_NULL_FUNCTION_FILTER}`;

const getHeadcountsByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  groupBy: DataFieldWithDataType,
  segmentBy: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const config: StoryboardItemConfig = {
    chartId: `headcount-by-dimension-${groupBy}-${segmentBy}`,
    layoutConfig: {
      ...gridConfig,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
      dataConfig: {
        title,
        query: {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
          groupBy: {
            type: 'non-hierarchical' as const,
            dataField: groupBy,
          },
          segmentBy: {
            type: 'non-hierarchical' as const,
            dataField: segmentBy,
          },
          filters: filters ?? undefined,
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths as const,
            input: { start: nowEndOfMonth, end: nowEndOfMonth },
          },
        },
      },
      selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
    },
  };
  return config;
};

export const yocAgencyStaffBarChartConfig = (
  storyboardDateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return getHeadcountsByDimensionChartConfig(
    storyboardDateUtils,
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
    },
    {
      [Languages.EN]:
        'YOC Agency Staff MP Capacity Chart(Excluding Operational Staff)',
      [Languages.JA]:
        'YOC Agency Staff MP Capacity Chart(Excluding Operational Staff)',
    },
    `${YOC_AGENCY_STAFF_MP_FILTER} AND ${NOT_OPERATIONAL_JOB_GRADE_FILTER}`,
    {
      gridColSpan: 3,
      gridRowSpan: 8,
    }
  );
};

export const jphilContractStaffBarChartConfig = (
  storyboardDateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  return getHeadcountsByDimensionChartConfig(
    storyboardDateUtils,
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_1,
    },
    {
      [Languages.EN]:
        'JPHIL Agency Staff (Contract Staff) MP Capacity Chart(Excluding PJ Staff assigned at local sites)',
      [Languages.JA]:
        'JPHIL Agency Staff (Contract Staff) MP Capacity Chart(Excluding PJ Staff assigned at local sites)',
    },
    JPHIL_CONTRACT_STAFF_FILTER,
    {
      gridColSpan: 3,
      gridRowSpan: 8,
    }
  );
};

export const getNikkiTotalNumberVsOperationalStaffFactbox = (
  storyboardDateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: StoryboardItemConfig = {
    chartId: 'total-number-vs-operational-staff-factbox',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 6,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: 'Total Number',
              [Languages.JA]: 'Total Number',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
              timeSelection,
              filters: `${YOC_AGENCY_STAFF_MP_FILTER} AND ${NOT_OPERATIONAL_JOB_GRADE_FILTER}`,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: 'Operational Staff',
              [Languages.JA]: 'Operational Staff',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
              timeSelection,
              filters: `${YOC_AGENCY_STAFF_MP_FILTER} AND ${OPERATIONAL_JOB_GRADE_FILTER}`,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
  return kpiConfig;
};

export const getNikkiTotalNumberFactbox = (
  storyboardDateUtils: StoryboardDateUtils
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: StoryboardItemConfig = {
    chartId: 'total-number-right-factbox',
    layoutConfig: {
      gridColSpan: 1,
      gridRowSpan: 6,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Total Number',
          [Languages.JA]: 'Total Number',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId' as const,
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection,
            filters: JPHIL_CONTRACT_STAFF_FILTER,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
  return kpiConfig;
};
